import { useQuery } from '@tanstack/react-query';

import { Fund } from '@/pages/setup-fund/utils/type';

import { TFetchError } from '../api-error.types';
import { _getWizardResumeQuery, WizardResumeParams } from './subscription-api';
import {
  projectSubscriptionToInvestment,
  SubscriptionStatus,
  WizardResumeResponse,
} from './subscription-projection';
import { Investment, InvestmentCurrency } from './types';

/**
 * Whether the invest wizard has a real backend subscription to run against. A
 * real public fund resolves only for funds that actually exist in the
 * investment context, so its presence is what says "this fund can hold a real
 * subscription".
 */
export const isRealInvestingFund = (realFund: Fund | null | undefined): realFund is Fund =>
  !!realFund;

export const AGREEMENT_ADVANCE_POLL_MS = 3000;
/** Roughly the panel's own signing poll budget (60 × 1s), which is the same wait. */
export const AGREEMENT_ADVANCE_POLL_WINDOW_MS = 60_000;

/**
 * How often to re-read the subscription while the investor's agreement is
 * signed but the backend has not moved it on yet.
 *
 * Signing is the one step no client can complete: Documenso's webhook advances
 * the subscription, so between "signed" and "advanced" there is a gap only the
 * server can close, and the signing panel's own poll has already stopped by
 * then. Bounded twice over — it ends as soon as the subscription leaves
 * `EligibilitySubmitted`, and gives up after the window so a webhook that never
 * arrives cannot leave the wizard polling forever.
 */
export const getAgreementAdvancePollInterval = (
  agreementSignedAt: number | null,
  resume: WizardResumeResponse | undefined,
  now: number = Date.now(),
): number | false => {
  if (agreementSignedAt === null) return false;
  if (resume?.subscription?.status !== SubscriptionStatus.EligibilitySubmitted) return false;

  return now - agreementSignedAt < AGREEMENT_ADVANCE_POLL_WINDOW_MS
    ? AGREEMENT_ADVANCE_POLL_MS
    : false;
};

/**
 * The wizard's resume read. Thin on purpose — the projection into the shape the
 * steps render is `projectSubscriptionToInvestment`'s job, so callers that only
 * need the fund's signing configuration are not forced through it.
 *
 * `agreementSignedAt` is when the signing panel reported the document signed,
 * and turns the read into the bounded poll above until the backend catches up.
 */
export const useWizardResume = (
  params: WizardResumeParams,
  {
    enabled = true,
    agreementSignedAt = null,
  }: { enabled?: boolean; agreementSignedAt?: number | null } = {},
) =>
  useQuery({
    ..._getWizardResumeQuery(params, enabled),
    refetchInterval: (query) =>
      getAgreementAdvancePollInterval(agreementSignedAt, query.state.data),
  });

// ==================== Entry resolution ====================

export interface WizardEntryInput {
  /** The `?investmentId` search param — a subscription id on the real path. */
  investmentId: string | null;
  /** The resolved public fund. */
  realFund: Fund | null | undefined;
  /** `useQuery(...).isLoading` for the public fund read. */
  isRealFundLoading: boolean;
  /** Why the public fund did not resolve — a 404 means no such fund, anything else is unknown. */
  realFundError?: unknown;
  resume: WizardResumeResponse | undefined;
  /** `useQuery(...).status` for the resume read. */
  resumeStatus: 'pending' | 'error' | 'success';
}

/**
 * What the wizard shell should render.
 *
 * `loading` is the public fund read itself still in flight — nothing else is
 * known yet. `notFound` is a fund identifier the investment context has no
 * record of; `blocked` is every other way the fund read could fail, including
 * no answer at all. The real views are the four outcomes of the resume read —
 * still loading, no subscription yet (amount entry), a subscription to
 * continue, or one that could not be read.
 */
export type WizardEntry =
  | { mode: 'loading' }
  | { mode: 'notFound' }
  | { mode: 'blocked' }
  | { mode: 'real'; view: 'loading' | 'error' | 'amount'; investment: null }
  | { mode: 'real'; view: 'steps'; investment: Investment };

/**
 * `404` is the discriminator's own answer — the fund is not one the investment
 * context serves publicly. Every other failure, including a network error
 * carrying no status at all, means the question went unanswered.
 */
const isFundAbsent = (error: unknown): boolean =>
  (error as TFetchError | undefined)?.status === 404 ||
  (error as TFetchError | undefined)?.data?.statusCode === 404;

/**
 * The wizard's entry decision, taken before any step renders.
 *
 * On the real path the URL's `investmentId` is a preference, not a
 * precondition: the resume read answers with the fund's active subscription
 * when no id was given (spec §6 resume-by-fund), so an investor who navigates
 * in from the fund page lands back on the step they left rather than being
 * offered a second subscription. An id that resolves to nothing is an error
 * rather than a fresh start — silently reopening amount entry would hide a
 * subscription the backend still considers live.
 */
export const resolveWizardEntry = ({
  investmentId,
  realFund,
  isRealFundLoading,
  realFundError,
  resume,
  resumeStatus,
}: WizardEntryInput): WizardEntry => {
  if (isRealFundLoading) return { mode: 'loading' };

  if (!isRealInvestingFund(realFund)) {
    return isFundAbsent(realFundError) ? { mode: 'notFound' } : { mode: 'blocked' };
  }

  if (resumeStatus === 'error') return { mode: 'real', view: 'error', investment: null };
  if (resumeStatus === 'pending' || !resume)
    return { mode: 'real', view: 'loading', investment: null };

  if (!resume.subscription) {
    return investmentId
      ? { mode: 'real', view: 'error', investment: null }
      : { mode: 'real', view: 'amount', investment: null };
  }

  // A subscription id names a subscription, not a fund: by-id resume answers
  // with any of the caller's own subscriptions, in any fund. Projecting it
  // under the route's fund would render another fund's subscription beside this
  // fund's name, payment instructions and wire details.
  if (resume.subscription.fundId !== realFund.id) {
    return { mode: 'real', view: 'error', investment: null };
  }

  return {
    mode: 'real',
    view: 'steps',
    investment: projectSubscriptionToInvestment(resume.subscription, realFund.id),
  };
};

// ==================== Fund configuration ====================

const WIZARD_CURRENCIES: InvestmentCurrency[] = ['USD', 'EUR'];

const isWizardCurrency = (currency: string | null | undefined): currency is InvestmentCurrency =>
  WIZARD_CURRENCIES.includes(currency as InvestmentCurrency);

/**
 * The fund configuration the wizard steps render.
 *
 * `id`/`identifier`/`name` are the real fund's own. `currency` is a
 * formattable stand-in for the facts the wizard still renders through it (the
 * closed-ended unit price): a fund denominated in something the wizard cannot
 * format falls back to a formattable one rather than rendering as raw text. It
 * is NOT what the investor's own amount is labelled with — that is
 * `toDisplayDenomination` — nor what the subscription is stored in, which is
 * `toSubscriptionDenomination`.
 *
 * `closedEnded`/`unitPrice` are always `false`/`0`: the public fund carries no
 * counterpart for either, and quoting one to a real investor would state a
 * fact about their money the fund never gave the wizard.
 */
export interface WizardFundConfig {
  id: string;
  identifier: string;
  name: string;
  currency: InvestmentCurrency;
  minInvestment: number;
  closedEnded: boolean;
  unitPrice: number;
}

export const toWizardFundConfig = (
  realFund: Fund | null | undefined,
  identifier: string,
): WizardFundConfig | undefined => {
  if (!realFund) return undefined;

  const minimum = realFund.minimumInvestment;

  return {
    id: realFund.id,
    identifier,
    name: realFund.name || identifier,
    currency: isWizardCurrency(minimum?.currency) ? minimum.currency : 'USD',
    minInvestment: minimum?.notApplicableMinimumInvestment
      ? 0
      : minimum?.valueMinimumInvestment || 0,
    closedEnded: false,
    unitPrice: 0,
  };
};

/** The route accepts 3–10 characters; anything else would 400 before the subscription exists. */
const DENOMINATION_LENGTH = { min: 3, max: 10 };

/**
 * What the subscription is stored as being denominated in — the real fund's own
 * currency, verbatim, including codes the wizard cannot format.
 *
 * Deliberately not `toWizardFundConfig`'s display currency: substituting there
 * costs a wrong symbol on screen, which the investor can see and report, while
 * substituting here would write a currency the fund does not use into the
 * subscription aggregate, where it is durable and silent. The fallback applies
 * only when the fund states nothing the route would accept at all.
 */
export const toSubscriptionDenomination = (
  realFund: Fund | null | undefined,
  fallback: string,
): string => {
  const declared = realFund?.minimumInvestment?.currency?.trim();

  return declared &&
    declared.length >= DENOMINATION_LENGTH.min &&
    declared.length <= DENOMINATION_LENGTH.max
    ? declared
    : fallback;
};

/**
 * What the investor's own money is labelled with on screen — the amount they
 * enter, the minimum they are held to, and the review rows they confirm.
 *
 * The real denomination wins over `toWizardFundConfig`'s display currency here,
 * even when the wizard has no symbol for it: substituting would state USD on
 * the amount the investor is committing while the subscription is written in
 * GBP, which no one on either side can see is wrong. `formatMoney` renders an
 * unrecognized code as a prefix (`GBP 1,000`) rather than failing, so there is
 * nothing to fall back for.
 */
export const toDisplayDenomination = (
  realDenomination: string | undefined,
  fallback: InvestmentCurrency,
): string => realDenomination?.trim() || fallback;

/**
 * The amount as the subscription routes accept it: a plain positive decimal
 * string. `String(1e-7)` would hand them `'1e-7'`, which their decimal pattern
 * rejects outright, so exponent notation is expanded; everything else is passed
 * through digit-for-digit rather than rounded to a currency's minor unit.
 */
export const toSubscriptionAmount = (amount: number): string => {
  const plain = String(amount);
  if (!plain.includes('e') && !plain.includes('E')) return plain;

  return amount.toFixed(18).replace(/0+$/, '').replace(/\.$/, '');
};
